import React, { useEffect, useState } from 'react';
import { FaSave } from 'react-icons/fa';

const PackageTypeTab = () => {
  const [packageTypes, setPackageTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalData, setModalData] = useState(null); // Data for Add/Edit modal
  const [deleteId, setDeleteId] = useState(null); // ID of package type to delete
  const [editMode, setEditMode] = useState(false); // Edit mode toggle
  const apiUrl = process.env.REACT_APP_BASE_URL;

  // Fetch package types
  const fetchPackageTypes = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/api/packageType`);
      if (!response.ok) throw new Error('Failed to fetch package types');
      const data = await response.json();
      setPackageTypes(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to fetch data');
      setPackageTypes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackageTypes();
  }, []);

  // Save or update a package type
  const handleSave = async (packageType) => {
    const method = editMode ? 'PUT' : 'POST';
    const url = editMode
      ? `${apiUrl}/api/packageType/${packageType.id}`
      : `${apiUrl}/api/packageType`;

    try {
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(packageType),
      });
      if (!response.ok) throw new Error('Error saving package type');
      await fetchPackageTypes();
      setModalData(null); // Close modal
    } catch (err) {
      setError('Error saving package type. Please try again.');
    }
  };

  // Delete a package type
  const handleDelete = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/packageType/${deleteId}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Error deleting package type');
      await fetchPackageTypes();
      setDeleteId(null); // Close delete confirmation modal
    } catch (err) {
      setError('Error deleting package type. Please try again.');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Package Types</h2>
        <button
          className="btn btn-primary"
          onClick={() => {
            setEditMode(false);
            setModalData({ name: '' }); // Open modal with empty data
          }}
        >
          Add Package Type
        </button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : error ? (
        <p className="text-red-500">{error}</p>
      ) : packageTypes.length === 0 ? (
        <p>No package types available.</p>
      ) : (
        <table className="table w-full border-collapse border border-gray-300">
          <thead>
            <tr>
              <th className="border border-gray-300 bg-base-200">Name</th>
              <th className="border border-gray-300 bg-base-200">Actions</th>
            </tr>
          </thead>
          <tbody>
            {packageTypes.map((packageType) => (
              <tr key={packageType.id}>
                <td>{packageType.name}</td>
                <td className="flex gap-2">
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => {
                      setEditMode(true);
                      setModalData(packageType); // Open modal with package type data
                    }}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => setDeleteId(packageType.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Add/Edit Modal */}
      {modalData && (
        <dialog className="modal modal-open bg-gray-50">
          <form
            className="modal-box"
            onSubmit={(e) => {
              e.preventDefault();
              handleSave(modalData);
            }}
          >
            <h3 className="font-bold text-lg">
              {editMode ? 'Edit Package Type' : 'Add Package Type'}
            </h3>
            <div className="form-control">
              <label htmlFor="name" className="label-text">
                Name
              </label>
              <input
                type="text"
                name="name"
                value={modalData.name || ''}
                onChange={(e) =>
                  setModalData((prev) => ({ ...prev, name: e.target.value }))
                }
                className="input input-bordered"
                required
              />
            </div>
            <div className="modal-action">
              <button
                type="button"
                className="btn"
                onClick={() => setModalData(null)} // Close modal
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                <FaSave />
                {editMode ? 'Update' : 'Save'}
              </button>
            </div>
          </form>
        </dialog>
      )}

      {/* Delete Confirmation Modal */}
      {deleteId && (
        <dialog className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">Confirm Deletion</h3>
            <p>Are you sure you want to delete this package type? This action cannot be undone.</p>
            <div className="modal-action">
              <button
                className="btn"
                onClick={() => setDeleteId(null)} // Close modal
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </dialog>
      )}
    </div>
  );
};

export default PackageTypeTab;
